import React, { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";

export function PanelSection({ panel, index, isFirst, testPrefix = "panel" }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start end", "end start"],
  });

  // Parallax più marcato + tilt 3D + zoom-in sul contenuto per un effetto
  // "immagine che si apre" quando il pannello entra e "si chiude" quando esce.
  const imgY = useTransform(scrollYProgress, [0, 1], ["-14%", "14%"]);
  const scale = useTransform(scrollYProgress, [0, 0.5, 1], [1.08, 1, 1.08]);
  const rotateX = useTransform(scrollYProgress, [0, 0.5, 1], [14, 0, -14]);
  const opacity = useTransform(scrollYProgress, [0, 0.18, 0.82, 1], [0, 1, 1, 0]);
  const contentY = useTransform(scrollYProgress, [0, 0.5, 1], [80, 0, -80]);
  const contentScale = useTransform(scrollYProgress, [0, 0.5, 1], [0.92, 1, 0.92]);

  const image = panel.image_url;
  // Absolute URL for images returned by our own backend (`/api/files/...`).
  const imageSrc = image && image.startsWith("/")
    ? `${process.env.REACT_APP_BACKEND_URL}${image}`
    : image;
  const isHero = index === 0;

  return (
    <section
      ref={ref}
      className="relative w-full overflow-hidden"
      style={{ height: "100vh", minHeight: 560, perspective: 1200, background: "#111" }}
      data-testid={`${testPrefix}-${index}`}
    >
      {/* Background image — using <img> so we can lazy-load below-the-fold
          panels. Hero (first panel) is eager + fetchpriority=high; the rest
          are lazy and decoded async. */}
      <motion.div
        className="absolute inset-0"
        style={{ y: imgY, scale }}
      >
        {imageSrc ? (
          <img
            src={imageSrc}
            alt=""
            aria-hidden="true"
            loading={isHero ? "eager" : "lazy"}
            decoding="async"
            {...(isHero ? { fetchpriority: "high" } : {})}
            className="w-full h-full object-cover"
            style={{ display: "block" }}
          />
        ) : (
          <div
            className="absolute inset-0 flex items-center justify-center"
            style={{ background: "linear-gradient(135deg, #d8d3c9 0%, #b5aea3 100%)" }}
          >
            <span
              className="text-[10px] uppercase tracking-[0.4em]"
              style={{ color: "rgba(0,0,0,0.35)" }}
            >
              Foto in arrivo
            </span>
          </div>
        )}
      </motion.div>

      {/* Dark overlay for readability */}
      <div
        className="absolute inset-0"
        style={{ background: "linear-gradient(180deg, rgba(0,0,0,0.25) 0%, rgba(0,0,0,0.55) 100%)" }}
      />

      {/* Content */}
      <motion.div
        className="relative h-full flex flex-col justify-end px-6 sm:px-12 pb-20 sm:pb-28"
        style={{ y: contentY, opacity, rotateX, scale: contentScale, transformStyle: "preserve-3d" }}
      >
        <div className="max-w-[1100px] mx-auto w-full">
          {panel.kicker ? (
            <div
              className="text-[11px] uppercase tracking-[0.3em] mb-5"
              style={{ color: "#FF4A26", fontWeight: 600, textShadow: "0 1px 12px rgba(0,0,0,0.7)" }}
              data-testid={`${testPrefix}-${index}-kicker`}
            >
              {panel.kicker}
            </div>
          ) : null}
          <h1
            className="text-5xl sm:text-7xl lg:text-8xl mb-6 max-w-[900px]"
            style={{
              color: "#FFFFFF",
              fontWeight: 300,
              letterSpacing: "-0.03em",
              lineHeight: 1.02,
            }}
            data-testid={`${testPrefix}-${index}-title`}
          >
            {panel.title}
          </h1>
          {panel.body ? (
            <p
              className="text-[17px] sm:text-[20px] max-w-[620px] mb-10"
              style={{ color: "rgba(255,255,255,0.85)", lineHeight: 1.5 }}
              data-testid={`${testPrefix}-${index}-body`}
            >
              {panel.body}
            </p>
          ) : null}

          <div className="flex flex-wrap items-center gap-4">
            {panel.cta_label && panel.cta_href ? (
              <PanelCTA
                label={panel.cta_label}
                href={panel.cta_href}
                variant="primary"
                testid={`${testPrefix}-${index}-cta`}
              />
            ) : null}
            {isFirst && panel.cta_secondary_label ? (
              <PanelCTA
                label={panel.cta_secondary_label}
                href={panel.cta_secondary_href}
                variant="ghost"
                testid={`${testPrefix}-${index}-cta-secondary`}
              />
            ) : null}
          </div>
        </div>
      </motion.div>
    </section>
  );
}

function PanelCTA({ label, href, variant, testid }) {
  const isExternal = /^https?:\/\//.test(href || "");
  const isHash = (href || "").startsWith("#");
  const cls =
    variant === "primary"
      ? { background: "#FF2800", color: "#FFFFFF" }
      : { background: "transparent", color: "#FFFFFF", border: "1px solid rgba(255,255,255,0.4)" };
  const shared =
    "inline-flex items-center gap-2 px-7 py-3.5 text-[14px] tracking-wide";
  if (isExternal) {
    return (
      <a href={href} target="_blank" rel="noreferrer" className={shared} style={cls} data-testid={testid}>
        {label}
      </a>
    );
  }
  if (isHash) {
    return (
      <a
        href={href}
        onClick={(e) => {
          const id = href.slice(1);
          const el = document.getElementById(id);
          if (el) {
            e.preventDefault();
            el.scrollIntoView({ behavior: "smooth", block: "start" });
          }
        }}
        className={shared}
        style={cls}
        data-testid={testid}
      >
        {label}
      </a>
    );
  }
  return (
    <Link to={href || "/"} className={shared} style={cls} data-testid={testid}>
      {label}
    </Link>
  );
}
